import React from 'react';
import Alerts from '../Alerts/Alerts';
import AdsCard from '../AdsCard/AdsCard';
import Heading from '../Typography/Heading';
import MainButton from '../MainButton/MainButton';

const SharePopup = ({
  isOpen,
  onClose,
  postTitle,
  currentUrl,
  shareType = 'post',
  variant = 'default',
  // Project-style (Dribbble) props
  previewImage,
  projectTitle,
  businessName,
  previewCompanyLogo,
  /** Listing data for project variant: renders AdsCard (same as service category page) */
  listingData,
}) => {
  if (!isOpen) return null;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(currentUrl).then(() => {
      Alerts.success('Link copied to clipboard');
      if (variant !== 'project') onClose();
    });
  };

  const handleSocialShare = (platform) => {
    let title = postTitle || projectTitle || 'Check out this post';
    if (shareType === 'profile') {
      title = 'Check out this profile';
    } else if (shareType === 'reviews') {
      title = 'Check out the reviews for this profile';
    }

    let shareUrl = '';
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(title)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`;
        break;
      case 'pinterest':
        shareUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(currentUrl)}&description=${encodeURIComponent(title)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(title + ' ' + currentUrl)}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(title)}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
    if (variant !== 'project') onClose();
  };

  const shareHeading = variant === 'project' ? 'Share Post' : 'Share this with your social Community';

  const socialButtonsDefault = [
    { key: 'facebook', label: 'Facebook', icon: 'fi-brands-facebook', bg: 'bg-[#1877F2] hover:bg-[#166FE5]' },
    { key: 'twitter', label: 'Twitter', icon: 'fi-brands-twitter', bg: 'bg-[#1DA1F2] hover:bg-[#1A94DA]' },
    { key: 'linkedin', label: 'LinkedIn', icon: 'fi-brands-linkedin', bg: 'bg-[#0A66C2] hover:bg-[#004182]' },
    { key: 'whatsapp', label: 'WhatsApp', icon: 'fi-brands-whatsapp', bg: 'bg-[#25D366] hover:bg-[#20BD5A]' },
    { key: 'telegram', label: 'Telegram', icon: 'fi-brands-telegram', bg: 'bg-[#0088CC] hover:bg-[#0077B5]' },
  ];

  const XIcon = () => (
    <svg viewBox="0 0 24 24" className="size-4 shrink-0" fill="currentColor" aria-hidden>
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.705 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
  const socialButtonsProject = [
    { key: 'linkedin', label: 'LinkedIn', icon: 'fi-brands-linkedin', iconColor: '#0A66C2' },
    { key: 'facebook', label: 'Facebook', icon: 'fi-brands-facebook', iconColor: '#1877F2' },
    { key: 'pinterest', label: 'Pinterest', icon: 'fi-brands-pinterest', iconColor: '#E60023' },
    { key: 'twitter', label: 'X', iconComponent: XIcon, iconColor: '#000000' },
  ];

  const socialButtons = variant === 'project' ? socialButtonsProject : socialButtonsDefault;

  if (variant === 'project') {
    const listing = listingData || {};
    const cardImages = (() => {
      try {
        if (listing?.content) {
          const contentArray = JSON.parse(listing.content);
          return Array.isArray(contentArray) ? contentArray : [];
        }
      } catch (_) {}
      return [];
    })();

    return (
      <div className="fixed inset-0 z-50 flex flex-col bg-black/60 backdrop-blur-sm">
        {/* Header bar: same height as Popup, close cross same size (text-base) and vertically centered */}
        <div className="flex-shrink-0 flex items-center justify-end h-[60px] sm:h-[80px] pr-4 sm:pr-6 z-10">
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center text-white hover:bg-white/10 transition-colors rounded-full p-2"
            aria-label="Close"
          >
            <i className="fi fi-rs-cross text-base cursor-pointer" />
          </button>
        </div>

        {/* Full-page content area (same layout as PostReportForm: width, heading, spacing) */}
        <div
          className="flex-1 overflow-y-auto bg-pastalBlueWhite rounded-t-xl min-h-0"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="min-h-full flex flex-col justify-center">
            <div className="w-[417px] mx-auto hidden-scrollbar lg:pb-0 relative">
            <Heading variant="h6" className="flex items-center justify-center text-center text-offblack">
              Share Post
            </Heading>
            <div className="mt-10 xl:mt-8 w-full">
            {/* Service card - fixed size 417 x 312.75 */}
            <div className="w-[417px] h-[312.75px] mx-auto overflow-hidden rounded-xl">
              <AdsCard
                isFeatured={true}
                serviceName={listing?.postTitle}
                serviceDescription={listing?.postDescription}
                category={listing?.mainCategory || listing?.user?.serviceCategory}
                Images={cardImages}
                companyName={listing?.user?.businessName}
                companyLogo={listing?.user?.profilePicture}
                serviceId={listing?.id}
                companyId={listing?.user?.id}
                likes={listing?.likes ?? 0}
                views={listing?.views ?? 0}
                userPlan={listing?.user?.userPlan?.plan?.name?.toLowerCase()}
                hideShare={true}
                hideUserInfo={true}
                hideHeart={true}
                hideLikes={true}
                hideViews={true}
                titleAlwaysVisible={true}
              />
            </div>

            {/* Social share row - same spacing as service detail: gap-4 */}
            <div className="flex justify-center gap-4 mt-10 xl:mt-8">
              {socialButtons.map(({ key, label, icon, iconImg, iconComponent: IconComponent, iconColor }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => handleSocialShare(key)}
                  className="w-10 h-10 rounded-full flex items-center justify-center border border-mediumgray hover:border-primaryblue transition-all shrink-0 p-0 bg-transparent"
                  title={`Share on ${label}`}
                  aria-label={`Share on ${label}`}
                >
                  <span
                    className="inline-flex items-center justify-center w-full h-full"
                    style={iconColor ? { color: iconColor } : undefined}
                  >
                    {IconComponent ? (
                      <IconComponent />
                    ) : iconImg ? (
                      <img src={iconImg} alt="" className="size-4 object-contain" aria-hidden />
                    ) : (
                      <i className={`fi ${icon} text-base leading-none inline-flex items-center justify-center w-full h-full`} aria-hidden />
                    )}
                  </span>
                </button>
              ))}
            </div>

            {/* Copy Link - MainButton secondary (outline) */}
            <div className="flex justify-center mt-10 xl:mt-8">
              <MainButton
                variant="outlinelargebetween"
                onClick={handleCopyLink}
                className="inline-flex items-center justify-center gap-2 px-5 md:px-6"
              >
                <i className="fi fi-rs-link text-xs leading-none inline-flex items-center justify-center mr-2" aria-hidden />
                <span>Copy Link</span>
              </MainButton>
            </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-12 pt-10 pb-8 border-b border-gray-100">
          <h2 className="text-2xl font-semibold text-offblack">
            {shareHeading}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-12 h-12 rounded-full text-gray-500 hover:text-offblack hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <i className="fi fi-rs-cross text-xl" />
          </button>
        </div>

        <div className="px-12 py-10">
          <div className="flex flex-wrap justify-center gap-8 mb-10">
            {socialButtons.map(({ key, label, icon, bg }) => (
              <button
                key={key}
                type="button"
                onClick={() => handleSocialShare(key)}
                className="flex flex-col items-center gap-4 group"
                title={`Share on ${label}`}
              >
                <span
                  className={`w-20 h-20 rounded-full inline-flex items-center justify-center text-white transition-transform group-hover:scale-105 ${bg}`}
                >
                  <i className={`fi ${icon} text-3xl leading-none inline-flex items-center justify-center`} />
                </span>
                <span className="text-sm font-medium text-gray-600 group-hover:text-offblack">
                  {label}
                </span>
              </button>
            ))}
          </div>

          <p className="text-base font-medium text-gray-600 mb-4">
            Or copy link
          </p>
          <div className="flex gap-4">
            <input
              type="text"
              readOnly
              value={currentUrl}
              className="flex-1 min-w-0 px-5 py-4 text-base border border-gray-200 rounded-xl bg-gray-50 text-offblack truncate"
            />
            <button
              type="button"
              onClick={handleCopyLink}
              className="shrink-0 px-6 py-4 rounded-xl font-medium text-base bg-primaryblue text-white hover:opacity-90 transition-opacity flex items-center gap-2"
            >
              <i className="fi fi-rs-link text-xs" />
              Copy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharePopup;
